import React from 'react';

export const BlogShopifyGuide = (props) => {
    return (
        <div className='blog-con'>
            <hr />
            <article>
                <p>Think of Liquid as the behind-the-scenes wizard of your Shopify theme. Not the dark, mysterious kind… more like a friendly wizard who hands you tools instead of riddles.</p>
                <p>Let’s walk through how to use Liquid to make your Shopify store look and behave exactly the way you want.</p>
                <section>
                    <h2>What Is Liquid?</h2>
                    <p>Liquid is Shopify’s open-source template language used to build and customize themes.</p>
                    <p>If HTML, CSS, and JavaScript are the trio from a superhero movie, then Liquid is the quiet genius in the chair telling everyone what to do.</p>
                    <ul>
                      <li><strong>HTML: </strong>“I structure everything.”</li>
                      <li><strong>CSS: </strong>“I make it pretty.”</li>
                      <li><strong>JavaScript: </strong>“I make things happen.”</li>
                      <li><strong>Liquid: </strong>“I bring the data… and the logic.”</li>
                    </ul>
                </section>
                <section>
                  <h3>Why Learn Liquid?</h3>
                  <p>Learning Liquid helps you:</p>
                  <ul>
                    <li>Customize Shopify themes beyond color changes and font tweaks</li>
                    <li>Add new layouts and dynamic features</li>
                    <li>Make your product pages look less like everyone else’s</li>
                    <li>Impress your friends by saying “I edited my Shopify theme code today”</li>
                  </ul>
                  <p>Trust me, that last one hits harder than it should.</p>
                </section>
                <section>
                  <h3>Where Liquid Lives in Shopify</h3>
                  <p>To access Liquid files, go to:</p>
                  <p><b>Shopify Dashboard → Online Store → Themes → Edit Code</b></p>
                  <p>You’ll find folders like:</p>
                  <ul>
                    <li><b>Sections</b></li>
                    <li><b>Snippets</b></li>
                    <li><b>Templates</b></li>
                  </ul>
                  <p>Basically, it’s like opening a closet that looks organized… until you start editing things. So let’s tread carefully.</p>
                </section>
                <section>
                  <h3>Liquid Basics (aka: The Part That Makes You Feel Like a Developer)</h3>
                  <p>Liquid uses three main markings:</p>
                  <h4>1. Output Tags</h4>
                  <p>These display content.<br />Like telling Shopify: “Hey, show the product title, please.”</p>
                  <pre><code>{`{{ product.title }}`}</code></pre>
                  <h4>2. Logic Tags</h4>
                  <p>Conditions, loops, logic… the stuff that makes you feel powerful.</p>
                  <pre><code>{`{% if product.available %}
  In stock!
{% else %}
  Sorry! Try again later.
{% endif %}
`}</code></pre>
                  <h4>3. Filters</h4>
                  <p>These transform output.<br />Think of them as Liquid’s version of makeup:</p>
                  <pre><code>{`{{ product.price | money }}
`}</code></pre>
                  <p>Poof — unformatted numbers become pretty currency.</p>
                </section>
                <section>
                  <h3>Real Ways to Customize Your Shopify Store With Liquid</h3>
                  <p>Here are practical examples with a pinch of comedy.</p>
                  <hr />
                  <h4>1. Add a Custom Message for Expensive Products</h4>
                  <p>Perfect for stores selling premium items (aka “I-bought-it-but-my-wallet-is-crying” products).</p>
                  <pre><code>{`{% if product.price > 10000 %}
  <p>This premium product includes free shipping (because your wallet deserves a break).</p>
{% endif %}
`}</code></pre>
                  <h4>2. Add a Low-Stock Warning That Creates FOMO</h4>
                  <p>Nothing says “Buy it now!” like a looming threat of disappointment.</p>
                  <pre><code>{`{% if product.inventory_quantity < 5 and product.inventory_quantity > 0 %}
  <p style="color:red;">Hurry! Only {{ product.inventory_quantity }} left in stock!</p>
{% endif %}
`}</code></pre>
                  <h4>3. Build a Custom Collection Grid</h4>
                  <p>More control over how your products appear.<br />Goodbye, boring grids. Hello, custom glory!</p>
                  <pre><code>{`{% for product in collection.products %}
  <div class="custom-product-card">
    <h3>{{ product.title }}</h3>
    <p>{{ product.price | money }}</p>
    <a href="{{ product.url }}">View Product</a>
  </div>
{% endfor %}
`}</code></pre>
                  <h4>4. Show Custom Fields (Metafields)</h4>
                  <p>Because sometimes products need to brag a little.</p>
                  <pre><code>{`{% if product.metafields.custom.material %}
  <p>Material: {{ product.metafields.custom.material }}</p>
{% endif %}
`}</code></pre>
                  <h4>5. Create Your Own Snippet (Like a Reusable Magic Spell)</h4>
                  <p><b>Step 1: </b>Create <code>custom-badge.liquid</code>:</p>
                  <pre><code>{`<p class="custom-badge">Best Seller</p>`}</code></pre>
                  <p><b>Step 2: </b>Summon it anywhere:</p>
                  <pre><code>{`{% render 'custom-badge' %}`}</code></pre>
                  <p>Boom. Magic.</p>
                </section>
                <section>
                  <h3>Best Practices (aka: How to Keep Your Theme From Exploding)</h3>
                  <p><strong>✔ Duplicate your theme before editing</strong><br />This is your safety net. Acrobatics optional.</p>
                  <p><strong>✔ Preview, don’t publish immediately</strong><br />Unless you enjoy chaos.</p>
                  <p><strong>✔ Use comments generously</strong><br />Your future self will thank you.</p>
                  <p><strong>✔ Avoid unnecessary edits to core files</strong><br />Stick to sections and snippets unless you’re feeling adventurous.</p>
                </section>
                <section>
                  <h3>Should You Hire a Developer?</h3>
                  <p>If you’re trying to:</p>
                  <ul>
                    <li>Build something totally custom</li>
                    <li>Add complex logic</li>
                    <li>Edit checkout (Shopify Plus only)</li>
                    <li>Or you’ve reached the “I broke something and I don’t know what I did” stage…</li>
                  </ul>
                  <p>Then yes, a developer might be your new best friend.</p>
                </section>
                <section>
                  <h3>Final Thoughts</h3>
                  <p>Liquid is the key to unlocking Shopify’s true potential. Once you get the hang of it, customizing your store becomes fun—almost addictive. You’ll be surprised how quickly you go from “What does this do?” to “Look what I just built!”</p>
                  <p>Happy coding! &#128521;</p>
                </section>

            </article>
        </div>
    );
};
